import type { PaginatedResponse } from '@repo/types/pagination';
import type { ReactNode } from 'react';

import { cn } from '@repo/lib/cn';
import { useEffect } from 'react';

import type { ParentListParams } from '@/hooks/parent';

import { ParentPagination } from '@/components/features/parent/ParentPagination';
import { EmptyState, EmptyStateContent, EmptyStateDescription } from '@/components/ui/empty-state';

interface ParentListSectionProps<TItem> {
  childId: number;
  data: PaginatedResponse<TItem>;
  listParams: ParentListParams;
  emptyMessage: string;
  children: (rows: TItem[]) => ReactNode;
  className?: string;
}

export function ParentListSection<TItem>(parentListSectionProps: ParentListSectionProps<TItem>) {
  const { childId, data, listParams, emptyMessage, children, className } = parentListSectionProps;

  const isFiltered = listParams.search.trim() !== '' || listParams.groupId !== undefined;

  const lastPage = Math.max(Math.ceil(data.total / data.pageSize) - 1, 0);
  const { setPage } = listParams;

  useEffect(() => {
    if (data.page > lastPage) {
      setPage(childId, lastPage);
    }
  }, [data.page, lastPage, childId, setPage]);

  if (data.total === 0) {
    return (
      <EmptyState>
        <EmptyStateContent>
          <EmptyStateDescription>{isFiltered ? 'Brak wyników dla wybranych filtrów' : emptyMessage}</EmptyStateDescription>
        </EmptyStateContent>
      </EmptyState>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {children(data.data)}
      <ParentPagination childId={childId} page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={(page) => listParams.setPage(childId, page)} />
    </div>
  );
}
